// NEW: src/components/concept-table-slide.jsx
//
// Reference table slide. Used to render concept-level visualSpec.tableData
// (currently only Trig's "specific-angle-values" concept). The table is
// shown ONCE up-front so students have the full reference before any
// derivation cards.
//
// Pure render — its own Prev/Next footer (mirrors question-bank-slide.jsx).
// The parent (EnhancedLearnMode) hides the shared Next/Back nav for this
// slide type so nav doesn't double up.

import { getFontFamily } from "../constants/colors";

const NOT_DEFINED_RE = /^not\s*defined$/i;

function Cell({ value, ff }) {
  const undef = typeof value === "string" && NOT_DEFINED_RE.test(value.trim());
  return (
    <td style={{
      padding: "12px 14px",
      border: "1px solid #EEEEEE",
      background: "#FFFFFF",
      textAlign: "center",
      fontFamily: ff,
      fontSize: 15,
      fontWeight: 400,
      color: undef ? "#999999" : "#222222",
      fontStyle: undef ? "italic" : "normal",
      whiteSpace: "nowrap",
    }}>
      {value}
    </td>
  );
}

export function ConceptTableSlide({ tableData, introLine, onNext, onPrev, lang }) {
  const ff = getFontFamily(lang);
  const headers = Array.isArray(tableData?.headers) ? tableData.headers : [];
  const rows = Array.isArray(tableData?.rows) ? tableData.rows : [];

  // Defensive: nothing to render — let the student advance instead of showing
  // a blank slide.
  if (headers.length === 0 || rows.length === 0) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: ff, color: "#888" }}>
        No table data
      </div>
    );
  }

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: "#FFFFFF",
      fontFamily: ff,
      width: "100%",
      minHeight: 0,
    }}>
      {/* Header strip */}
      <div style={{
        padding: "16px 20px",
        borderBottom: "1px solid #EEEEEE",
        background: "#FFFFFF",
        textAlign: "center",
        flexShrink: 0,
      }}>
        <div style={{
          fontSize: 18,
          fontWeight: 600,
          color: "#6AAAC8",
          fontFamily: ff,
          letterSpacing: "-0.01em",
        }}>
          Reference Table
        </div>
      </div>

      {/* Scroll body */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "20px 16px 24px",
        WebkitOverflowScrolling: "touch",
      }}>
        {introLine && (
          <div style={{
            fontSize: 14,
            color: "#666666",
            fontStyle: "italic",
            textAlign: "center",
            maxWidth: 320,
            margin: "0 auto 18px",
            lineHeight: 1.5,
            fontFamily: ff,
          }}>
            {introLine}
          </div>
        )}

        {/* Horizontal scroll wrapper for narrow screens */}
        <div style={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          border: "1.5px solid #DDDDDD",
          borderRadius: 8,
          margin: "0 auto",
          maxWidth: "100%",
        }}>
          <table style={{
            borderCollapse: "collapse",
            width: "100%",
            fontFamily: ff,
          }}>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} style={{
                    padding: "12px 14px",
                    background: "#6AAAC8",
                    color: "#FFFFFF",
                    fontFamily: ff,
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: "center",
                    border: "1px solid #6AAAC8",
                    whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const values = Array.isArray(row?.values) ? row.values : [];
                return (
                  <tr key={ri}>
                    <td style={{
                      padding: "12px 14px",
                      border: "1px solid #EEEEEE",
                      background: "#FDFBF7",
                      textAlign: "center",
                      fontFamily: ff,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#3D2E15",
                      whiteSpace: "nowrap",
                    }}>
                      {row?.label}
                    </td>
                    {values.map((v, ci) => (
                      <Cell key={ci} value={v} ff={ff} />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{
          marginTop: 16,
          fontSize: 13,
          color: "#888888",
          fontStyle: "italic",
          textAlign: "center",
          maxWidth: 360,
          margin: "16px auto 0",
          lineHeight: 1.5,
          fontFamily: ff,
        }}>
          Memorise this — appears in nearly every Trigonometry board question.
        </div>
      </div>

      {/* Footer nav */}
      <div style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderTop: "1px solid #EEEEEE",
        background: "#FFFFFF",
        flexShrink: 0,
        boxSizing: "border-box",
      }}>
        <button
          onClick={onPrev}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: "10px 14px",
            background: "transparent",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ff,
            color: "#6AAAC8",
            cursor: "pointer",
          }}>
          ‹ Previous
        </button>
        <button
          onClick={onNext}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: "10px 20px",
            background: "#6AAAC8",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ff,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(106,170,200,0.3)",
          }}>
          Next ›
        </button>
      </div>
    </div>
  );
}

export default ConceptTableSlide;
